import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";

declare global {
  interface Window {
    google?: {
      accounts?: {
        id?: {
          initialize: (options: { client_id: string; callback: (r: { credential?: string }) => void }) => void;
          renderButton: (parent: HTMLElement, options: Record<string, unknown>) => void;
        };
      };
    };
  }
}

type EmailQueueRow = {
  id: string;
  createdAt: string;
  updatedAt: string;
  scheduledAt: string;
  lockedAt: string | null;
  sentAt: string | null;
  status: "queued" | "processing" | "sent" | "failed" | "canceled";
  attempts: number;
  maxAttempts: number;
  registrationId: string | null;
  eventSlug: string | null;
  recipientEmail: string;
  emailType: string;
  subject: string;
  lastError: string | null;
  metadata: Record<string, unknown> | null;
};

const tokenStorageKey = "aurays_admin_token";

const statusBadgeClass: Record<EmailQueueRow["status"], string> = {
  queued: "bg-gray-100 text-gray-700",
  processing: "bg-amber-50 text-amber-700",
  sent: "bg-emerald-50 text-emerald-700",
  failed: "bg-red-50 text-red-700",
  canceled: "bg-gray-200 text-gray-600",
};

export default function EmailQueuePage() {
  const [token, setToken] = useState("");
  const [authed, setAuthed] = useState(false);
  const googleButtonRef = useRef<HTMLDivElement | null>(null);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [rows, setRows] = useState<EmailQueueRow[]>([]);
  const [retryingId, setRetryingId] = useState<string | null>(null);

  const [filterStatus, setFilterStatus] = useState("");
  const [filterEmailType, setFilterEmailType] = useState("");
  const [filterEventSlug, setFilterEventSlug] = useState("");
  const [filterRecipient, setFilterRecipient] = useState("");

  const [selected, setSelected] = useState<EmailQueueRow | null>(null);

  const authHeaders = useMemo(() => {
    const t = token.trim();
    return t ? { Authorization: `Bearer ${t}` } : {};
  }, [token]);

  const setAdminToken = (nextToken: string) => {
    const t = nextToken.trim();
    if (t) {
      sessionStorage.setItem(tokenStorageKey, t);
      setToken(t);
    }
  };

  useEffect(() => {
    const savedToken = sessionStorage.getItem(tokenStorageKey);
    if (savedToken) {
      setToken(savedToken);
      setAuthed(true);
    }
  }, []);

  const loginWithGoogleIdToken = useCallback(async (idToken: string) => {
    setError("");
    setLoading(true);
    try {
      const res = await fetch("/api/admin/login/google", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ idToken }),
      });
      const data = (await res.json().catch(() => null)) as {
        ok?: boolean;
        token?: string | null;
        error?: string;
      } | null;
      if (!res.ok || !data?.ok || typeof data.token !== "string" || !data.token.trim()) {
        setAuthed(false);
        setError(data?.error || "Google login failed.");
        return;
      }
      setAdminToken(data.token);
      setAuthed(true);
    } catch {
      setAuthed(false);
      setError("Google login failed.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (authed) return;
    const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID as string | undefined;
    const container = googleButtonRef.current;
    if (!clientId || !container) return;

    const render = () => {
      const google = window.google?.accounts?.id;
      if (!google?.initialize || !google?.renderButton) return;
      container.innerHTML = "";
      google.initialize({
        client_id: clientId,
        callback: (response) => {
          const credential = typeof response?.credential === "string" ? response.credential : "";
          if (credential) void loginWithGoogleIdToken(credential);
        },
      });
      google.renderButton(container, {
        type: "standard",
        theme: "outline",
        size: "large",
        width: 320,
        text: "signin_with",
        shape: "pill",
      });
    };

    if (window.google?.accounts?.id) {
      render();
      return;
    }

    const existing = document.getElementById("google-gsi");
    if (existing) {
      const interval = window.setInterval(() => {
        if (window.google?.accounts?.id) {
          window.clearInterval(interval);
          render();
        }
      }, 50);
      return () => window.clearInterval(interval);
    }

    const script = document.createElement("script");
    script.id = "google-gsi";
    script.async = true;
    script.defer = true;
    script.src = "https://accounts.google.com/gsi/client";
    script.onload = () => render();
    document.head.appendChild(script);
  }, [authed, loginWithGoogleIdToken]);

  const loadQueue = useCallback(async () => {
    if (!authed || !token.trim()) return;
    setError("");
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterStatus) params.set("status", filterStatus);
      if (filterEmailType) params.set("emailType", filterEmailType);
      if (filterEventSlug) params.set("eventSlug", filterEventSlug);
      if (filterRecipient) params.set("recipientEmail", filterRecipient);
      params.set("limit", "500");

      const res = await fetch(`/api/admin/email-queue?${params.toString()}`, { headers: authHeaders });
      if (!res.ok) throw new Error("Request failed");
      const data = (await res.json()) as { ok?: boolean; rows?: EmailQueueRow[] };
      if (!data.ok || !Array.isArray(data.rows)) throw new Error("Request failed");
      setRows(data.rows);
    } catch {
      setError("Failed to load email queue.");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [authHeaders, authed, filterEmailType, filterEventSlug, filterRecipient, filterStatus, token]);

  const retryEmail = useCallback(
    async (id: string) => {
      if (!authed || !token.trim()) return;
      setError("");
      setRetryingId(id);
      try {
        const res = await fetch(`/api/admin/email-queue/${encodeURIComponent(id)}/retry`, {
          method: "POST",
          headers: authHeaders,
        });
        const data = (await res.json().catch(() => null)) as { ok?: boolean; error?: string } | null;
        if (!res.ok || !data?.ok) {
          throw new Error(data?.error || "Request failed");
        }
        await loadQueue();
      } catch {
        setError("Failed to retry email.");
      } finally {
        setRetryingId(null);
      }
    },
    [authHeaders, authed, loadQueue, token],
  );

  useEffect(() => {
    if (!authed || !token.trim()) return;
    void loadQueue();
  }, [authed, token, loadQueue]);

  return (
    <div className="min-h-screen bg-[#FAFAF9]">
      <Navbar />

      <section className="pt-28 pb-20 md:pt-36 md:pb-28">
        <div className="w-[90%] mx-auto">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-8">
            <div>
              <p className="text-sm font-semibold uppercase tracking-[0.3em] text-[#9B72FF] mb-3">Email Queue</p>
              <h1 className="text-3xl md:text-4xl font-bold text-gray-900 leading-tight">Email Ops</h1>
              <p className="text-gray-500 text-sm md:text-base leading-relaxed mt-3 max-w-2xl">
                View all queued, processing, sent, and failed emails.
              </p>
            </div>

            {!authed ? (
              <div className="max-w-md">
                {import.meta.env.VITE_GOOGLE_CLIENT_ID ? (
                  <div className="mb-3">
                    <div ref={googleButtonRef} className="min-h-[44px]" />
                  </div>
                ) : (
                  <p className="text-sm text-red-600">Missing VITE_GOOGLE_CLIENT_ID. Google login is not configured.</p>
                )}
                {error ? <p className="text-sm text-red-600 mt-3">{error}</p> : null}
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <button
                  onClick={() => void loadQueue()}
                  disabled={loading}
                  className={`px-4 py-2 rounded-full text-sm font-semibold text-white transition-colors whitespace-nowrap ${
                    loading ? "bg-gray-300 cursor-not-allowed" : "bg-gray-900 hover:bg-gray-700"
                  }`}
                >
                  {loading ? "Loading..." : "Refresh"}
                </button>
              </div>
            )}
          </div>

          {authed ? (
            <>
              <div className="rounded-3xl border border-gray-100 bg-white p-5 mb-5">
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
                  <select
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                    className="border border-gray-200 rounded-2xl px-4 py-3 text-sm text-gray-900 bg-white"
                  >
                    <option value="">All Status</option>
                    <option value="queued">Queued</option>
                    <option value="processing">Processing</option>
                    <option value="sent">Sent</option>
                    <option value="failed">Failed</option>
                    <option value="canceled">Canceled</option>
                  </select>

                  <input
                    type="text"
                    value={filterEmailType}
                    onChange={(e) => setFilterEmailType(e.target.value)}
                    placeholder="Email type (e.g. approved_ticket)"
                    className="border border-gray-200 rounded-2xl px-4 py-3 text-sm text-gray-900"
                  />

                  <input
                    type="text"
                    value={filterEventSlug}
                    onChange={(e) => setFilterEventSlug(e.target.value)}
                    placeholder="Event slug"
                    className="border border-gray-200 rounded-2xl px-4 py-3 text-sm text-gray-900"
                  />

                  <input
                    type="text"
                    value={filterRecipient}
                    onChange={(e) => setFilterRecipient(e.target.value)}
                    placeholder="Recipient email (search)"
                    className="border border-gray-200 rounded-2xl px-4 py-3 text-sm text-gray-900"
                  />
                </div>

                {error ? <p className="text-sm text-red-600 mt-4">{error}</p> : null}
              </div>

              <div className="rounded-3xl border border-gray-100 bg-white overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead className="bg-gray-50 text-gray-500">
                      <tr>
                        <th className="text-left font-semibold px-4 py-3">Created</th>
                        <th className="text-left font-semibold px-4 py-3">Status</th>
                        <th className="text-left font-semibold px-4 py-3">Recipient</th>
                        <th className="text-left font-semibold px-4 py-3">Type</th>
                        <th className="text-left font-semibold px-4 py-3">Subject</th>
                        <th className="text-left font-semibold px-4 py-3">Attempts</th>
                        <th className="text-left font-semibold px-4 py-3">Event</th>
                        <th className="text-left font-semibold px-4 py-3">Last Error</th>
                        <th className="text-left font-semibold px-4 py-3"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {rows.map((row) => (
                        <tr key={row.id} className="hover:bg-gray-50">
                          <td className="px-4 py-3 whitespace-nowrap text-gray-700">
                            {new Date(row.createdAt).toLocaleString()}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <span className={`inline-flex px-3 py-1 rounded-full text-xs font-semibold ${statusBadgeClass[row.status]}`}>
                              {row.status}
                            </span>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-gray-700">{row.recipientEmail}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-gray-700">{row.emailType}</td>
                          <td className="px-4 py-3 text-gray-700 min-w-[240px]">{row.subject}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-gray-700">
                            {row.attempts}/{row.maxAttempts}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-gray-700">{row.eventSlug || "-"}</td>
                          <td className="px-4 py-3 text-gray-500 min-w-[220px]">
                            {row.lastError ? row.lastError : "-"}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <div className="flex items-center gap-2">
                              {row.status === "failed" ? (
                                <button
                                  onClick={() => void retryEmail(row.id)}
                                  disabled={loading || retryingId === row.id}
                                  className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
                                    loading || retryingId === row.id
                                      ? "bg-gray-200 text-gray-600 cursor-not-allowed"
                                      : "bg-red-50 text-red-700 hover:bg-red-100"
                                  }`}
                                >
                                  {retryingId === row.id ? "Retrying..." : "Retry"}
                                </button>
                              ) : null}
                              <button
                                onClick={() => setSelected(row)}
                                className="px-3 py-1.5 rounded-full text-xs font-semibold bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
                              >
                                View
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                      {rows.length === 0 ? (
                        <tr>
                          <td colSpan={9} className="px-4 py-10 text-center text-gray-500">
                            {loading ? "Loading..." : "No emails found."}
                          </td>
                        </tr>
                      ) : null}
                    </tbody>
                  </table>
                </div>
              </div>

              {selected ? (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 px-4">
                  <div className="w-full max-w-3xl bg-white rounded-3xl border border-gray-100 shadow-xl overflow-hidden">
                    <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
                      <div>
                        <p className="text-sm text-gray-500">Email</p>
                        <p className="text-lg font-semibold text-gray-900">{selected.subject}</p>
                      </div>
                      <button
                        onClick={() => setSelected(null)}
                        className="w-10 h-10 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition-colors"
                      >
                        <i className="ri-close-line text-xl text-gray-700"></i>
                      </button>
                    </div>
                    <div className="px-6 py-5 grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                      <div className="rounded-2xl bg-gray-50 p-4">
                        <p className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-2">Recipient</p>
                        <p className="font-medium text-gray-900 break-all">{selected.recipientEmail}</p>
                        <p className="text-gray-500 mt-1">{selected.emailType}</p>
                      </div>
                      <div className="rounded-2xl bg-gray-50 p-4">
                        <p className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-2">Status</p>
                        <p className="font-medium text-gray-900">
                          {selected.status} ({selected.attempts}/{selected.maxAttempts})
                        </p>
                        <p className="text-gray-500 mt-1">Scheduled: {new Date(selected.scheduledAt).toLocaleString()}</p>
                      </div>
                      <div className="rounded-2xl bg-gray-50 p-4">
                        <p className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-2">Event</p>
                        <p className="font-medium text-gray-900 break-all">{selected.eventSlug || "-"}</p>
                        <p className="text-gray-500 mt-1 break-all">Registration: {selected.registrationId || "-"}</p>
                      </div>
                      <div className="rounded-2xl bg-gray-50 p-4">
                        <p className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-2">Times</p>
                        <p className="text-gray-700">Created: {new Date(selected.createdAt).toLocaleString()}</p>
                        <p className="text-gray-700">Sent: {selected.sentAt ? new Date(selected.sentAt).toLocaleString() : "-"}</p>
                        <p className="text-gray-700">Locked: {selected.lockedAt ? new Date(selected.lockedAt).toLocaleString() : "-"}</p>
                      </div>
                      <div className="md:col-span-2 rounded-2xl bg-gray-50 p-4">
                        <p className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-2">Last Error</p>
                        <p className="text-gray-700 whitespace-pre-wrap break-words">{selected.lastError || "-"}</p>
                      </div>
                      <div className="md:col-span-2 rounded-2xl bg-gray-50 p-4">
                        <p className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-2">Metadata</p>
                        <pre className="text-xs text-gray-700 whitespace-pre-wrap break-words">
                          {JSON.stringify(selected.metadata || {}, null, 2)}
                        </pre>
                      </div>
                    </div>
                  </div>
                </div>
              ) : null}
            </>
          ) : null}
        </div>
      </section>

      <Footer />
    </div>
  );
}
